<template>
  <Component
    :is="tag"
    :disabled="disabled || loading"
    :type="btnType"
    :to="to"
    :class="classNames"
    @click="handleClick"
  >
    <span v-if="loading" :class="[rootClass + '__loader']">
      <slot name="loading">
        <BaseSpinner
          :class="[rootClass + '__spinner']"
          :size="size === 'sm' ? '16px' : '20px'"
        />
      </slot>
    </span>
    <span :class="[rootClass + '__inner']">
      <slot></slot>
    </span>
  </Component>
</template>
<script>
const BTN_CLASS_NAME = "base-btn";
export default {
  name: "AppButton",
  props: {
    to: [String, Object],
    tag: {
      type: String,
      default: "button",
    },
    type: {
      type: String,
      default: "button",
    },
    loading: Boolean,
    disabled: Boolean,
    theme: {
      // Primary, Secondary, Subtle, Text, primary-border
      type: String,
      default: "primary",
    },
    block: {
      type: Boolean,
      default: false,
    },
    size: {
      // lg, md, sm
      type: String,
      default: "md",
    },
    xSpace: {
      // lg, md, sm
      type: String,
      default: "",
    },
    shape: {
      type: String,
      default: "round",
    },
  },

  computed: {
    btnType() {
      const { type, tag } = this;
      return tag === "button" ? type : null;
    },
    rootClass() {
      return BTN_CLASS_NAME;
    },
    classNames() {
      const { disabled, loading, block, theme, size, xSpace, shape } = this;
      const result = [
        BTN_CLASS_NAME,
        `${BTN_CLASS_NAME}--theme-${theme}`,
        `${BTN_CLASS_NAME}--size-${size}`,
        `${BTN_CLASS_NAME}--shape-${shape}`,
        {
          "is-disabled": disabled,
          "is-loading": loading,
          [`${BTN_CLASS_NAME}--block`]: block,
        },
      ];
      if (xSpace) {
        result.push(`${BTN_CLASS_NAME}--xspace-${xSpace}`);
      }
      return result;
    },
  },

  methods: {
    handleClick(event) {
      const { disabled, loading } = this;
      if (disabled || loading) {
        event.preventDefault();
      } else {
        this.$emit("click", event);
      }
    },
  },
};
</script>

<style lang="less" scoped>
@btn-height: 44px;
@btn-sm-height: 36px;
@btn-xs-height: 30px;
@btn-xspase: 30px;
@btn-radius: 30px;

.base-btn {
  height: @btn-height;
  display: inline-block;
  line-height: @btn-height - 2px; // border-compensation
  padding: 0 @btn-xspase;
  border-width: 1px;
  border-style: solid;
  border-color: @base-color;
  border-radius: @btn-radius;
  color: @base-color;
  font-family: inherit;
  outline: none;
  background: transparent;
  transition: background 0.3s, border 0.3s, color 0.3s;
  text-decoration: none;
  text-align: center;
  font-weight: 700;
  white-space: nowrap;
  user-select: none;
  outline-offset: 0;
  cursor: pointer;
  position: relative;
  min-width: 100px;
  font-size: 14px;
  text-transform: uppercase;

  &__inner {
    opacity: 1;
    transition: opacity 0.3s;
  }

  &.is-loading &__inner {
    opacity: 0;
  }

  &__spinner {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  &:focus {
    outline: none;
    // box-shadow: @shadow-focus;
  }

  &:active {
    transform: translateY(1px);
    box-shadow: none;
  }

  // &:hover {
  //   border-color: darken(@primary-color, 10%);
  //   background-color: darken(@primary-color, 10%);
  // }

  &--theme-primary {
    background-color: @primary-color;
    border-color: @primary-color;
    color: #fff;

    &:hover {
      background-color: darken(@primary-color, 10%);
      border-color: darken(@primary-color, 10%);
    }

    &.is-disabled {
      background-color: lighten(@disabled-color, 8%);
      border-color: @disabled-color;
      color: darken(@disabled-color, 28%);
    }
  }

  &--theme-primary &__spinner /deep/ .base-spinner__rotator {
    border-color: transparent white white !important;
  }

  &--theme-secondary {
    background: #fff;
    border-color: @primary-color;
    color: @primary-color;

    &:hover {
      border-color: darken(@primary-color, 10%);
      color: darken(@primary-color, 10%);
    }
  }

  &--theme-azure {
    background: @info-color;
    border-color: @info-color;
    color: #fff;

    &:hover {
      border-color: darken(@info-color, 10%);
      background: darken(@info-color, 10%);
    }
  }

  &--shape-square {
    border-radius: @border-radius-xs;
  }

  &--block {
    display: block;
    width: 100%;
    text-align: center;
  }

  &.is-loading {
    cursor: default;

    &:active {
      transform: none;
    }
  }

  &.is-disabled {
    pointer-events: none;
    cursor: not-allowed;

    &:active {
      transform: none;
    }
  }

  &--size-sm {
    height: @btn-sm-height;
    line-height: @btn-sm-height - 2px;
    text-transform: none;
    padding: 0 20px;

    @media (max-width: @screen-xs) {
      & {
        font-size: 13px;
      }
    }
  }

  &--size-xs {
    height: @btn-xs-height;
    line-height: @btn-xs-height - 2px;
    text-transform: none;
    padding: 0 10px;
  }
}
</style>
